/**
 * +1 Guest Utilities
 * Handles pricing, validation, and logic for +1 guest feature
 */

import { PlusOneGuestInfo } from '~types/subscription/subscription.types';

/**
 * Parse +1 guest fee to numeric value in minor currency units (pence for GBP)
 * @param fee - Fee string or number (e.g., '£2', 'Free', 2, 0)
 * @returns Numeric fee in minor units (e.g., 200 for £2, 0 for Free)
 */
export function parsePlusOneGuestFee(fee: string | number | undefined): number {
  if (!fee) return 0;
  
  if (typeof fee === 'number') {
    return Math.round(fee * 100); // Convert to minor units
  }

  if (typeof fee === 'string') {
    if (fee.toLowerCase() === 'free') return 0;
    
    // Extract numeric value from string (e.g., '£2' -> 2)
    const match = fee.match(/[\d.]+/);
    if (match) {
      const numericValue = parseFloat(match[0]);
      return Math.round(numericValue * 100); // Convert to minor units
    }
  }

  return 0;
}

/**
 * Format +1 guest fee for display
 * @param fee - Fee string or number
 * @param currency - Currency symbol (e.g., '£', '$')
 * @returns Formatted fee string
 */
export function formatPlusOneGuestFee(fee: string | number | undefined, currency: string = '£'): string {
  if (!fee) return `${currency}0`;
  
  if (typeof fee === 'string') {
    if (fee.toLowerCase() === 'free') return 'Free';
    return fee;
  }

  if (typeof fee === 'number') {
    if (fee === 0) return 'Free';
    return `${currency}${fee.toFixed(2)}`;
  }

  return `${currency}0`;
}

/**
 * Check if event type is excluded from +1 guest benefit
 * @param eventServiceType - Event service type (e.g., 'THE_REST_TABLE', 'HOUSEWARMING')
 * @param excludedEventTypes - List of excluded event types
 * @returns True if event is excluded
 */
export function isPlusOneGuestExcludedForEvent(
  eventServiceType: string | undefined,
  excludedEventTypes?: string[]
): boolean {
  if (!excludedEventTypes || !eventServiceType) return false;
  return excludedEventTypes.includes(eventServiceType);
}

/**
 * Calculate +1 guest cost for booking
 * @param quantity - Number of +1 guests to add
 * @param plusOneInfo - +1 guest info from subscription
 * @returns Total cost in minor units (pence for GBP)
 */
export function calculatePlusOneGuestCost(quantity: number, plusOneInfo?: PlusOneGuestInfo): number {
  if (!plusOneInfo || !plusOneInfo.enabled || quantity <= 0) return 0;
  
  const feePerGuest = parsePlusOneGuestFee(plusOneInfo.fee);
  return feePerGuest * quantity;
}

/**
 * Check if +1 guest can be added for event
 * @param eventServiceType - Event service type
 * @param hasActiveSubscription - Whether user has active subscription
 * @param plusOneInfo - +1 guest info
 * @returns True if +1 guest can be added
 */
export function canAddPlusOneGuest(
  eventServiceType: string | undefined,
  hasActiveSubscription: boolean,
  plusOneInfo?: PlusOneGuestInfo
): boolean {
  console.log(eventServiceType, 'eventServiceTypeeventServiceType');
  
  if (!hasActiveSubscription || !plusOneInfo || !plusOneInfo.enabled) {
    return false;
  }

  // Check if event is excluded
  if (isPlusOneGuestExcludedForEvent(eventServiceType, plusOneInfo.excludedEventTypes)) {
    return false;
  }

  return true;
}

/**
 * Get +1 guest registration deadline days requirement
 * @param plusOneInfo - +1 guest info
 * @returns Number of days required before event
 */
export function getPlusOneGuestRegistrationDaysRequired(plusOneInfo?: PlusOneGuestInfo): number {
  return plusOneInfo?.registrationDaysRequired || 3; // Default to 3 days
}

/**
 * Format +1 guest note for display
 * @param fee - Fee value
 * @param currency - Currency symbol
 * @param daysRequired - Days required for registration
 * @returns Formatted note string
 */
export function formatPlusOneGuestNote(
  fee: string | number | undefined,
  currency: string = '£',
  daysRequired: number = 3
): string {
  const formattedFee = formatPlusOneGuestFee(fee, currency);
  
  if (formattedFee === 'Free') {
    return `👥 +1 Guest Benefit: Invite a friend for free! They must register at least ${daysRequired} days before the event.`;
  }
  
  return `👥 +1 Guest Benefit: Bring a friend for ${formattedFee} per event. They must register at least ${daysRequired} days before the event.`;
}

/**
 * Get +1 guest warning message if registration deadline not met
 * @param daysRequired - Days required for registration
 * @returns Warning message
 */
export function getPlusOneGuestRegistrationWarning(daysRequired: number = 3): string {
  return `⚠️ Note: Your +1 guest must register their details at least ${daysRequired} days before the event. If they fail to register in time, they will lose their seat and a no-show fee may apply.`;
}
